// V4-FRONT-ANY-USAGE batch 4 2026-05-28
/**
 * V4-ZERNIO-ANALYTICS-POST-CARD 2026-05-28 — Modale drill-down analytics par post.
 *
 * Affiche le snapshot courant + l'historique (period 30j par défaut) des analytics
 * d'un post publié via Zernio. Appelée depuis PostCard quand status === 'published'.
 */
import { useEffect, useState } from 'react';
import { X, Loader2, Eye, Heart, MessageCircle, TrendingUp, Users, BarChart3 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Logger } from '../../../utils/logger';
import { toastService } from '../../../services/toastService';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  zernioAnalyticsService,
  type PostAnalyticsResponse,
  type PostAnalyticsSnapshot,
} from '../../../services/zernioAnalyticsService';
import { formatDateLong } from '../../../utils/dateUtils';

interface PostAnalyticsModalProps {
  postId: string;
  postTitle?: string;
  onClose: () => void;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(Math.round(n));
}

function formatPct(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`;
}

function Metric({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div
      className="rounded-xl p-3 flex flex-col gap-1"
      style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
      }}
    >
      <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
        {value}
      </div>
    </div>
  );
}

export function PostAnalyticsModal({ postId, postTitle, onClose }: PostAnalyticsModalProps) {
  const { t } = useLanguage();
  const tp = t.postAnalytics;
  const [data, setData] = useState<PostAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await zernioAnalyticsService.getPostAnalytics(postId, 30);
        if (!cancelled) setData(res);
      } catch (e) {
        Logger.error('[PostAnalyticsModal] load failed', e);
        if (!cancelled) {
          setError(tp.loadError);
          toastService.error(tp.loadError);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [postId, tp.loadError]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await zernioAnalyticsService.refreshPostAnalytics(postId);
      const res = await zernioAnalyticsService.getPostAnalytics(postId, 30);
      setData(res);
      toastService.success(tp.refreshed);
    } catch (e) {
      Logger.error('[PostAnalyticsModal] refresh failed', e);
      toastService.error(tp.refreshFailed);
    } finally {
      setRefreshing(false);
    }
  };

  const current: PostAnalyticsSnapshot = data?.current ?? {
    impressions: 0, reach: 0, views: 0, likes: 0,
    comments_count: 0, shares: 0, saves: 0, clicks: 0, engagement_rate: 0,
  };
  const history = data?.history ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-2xl shadow-xl flex flex-col max-h-[85vh]"
        style={{
          backgroundColor: 'var(--color-background)',
          border: '1px solid var(--color-border)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 flex-shrink-0"
          style={{ borderBottom: '1px solid var(--color-border)' }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <BarChart3 className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-primary)' }} />
            <h2
              className="truncate"
              style={{ color: 'var(--color-text-primary)' }}
              title={postTitle}
            >
              {tp.title}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              className="rounded-full"
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : tp.refresh}
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-primary)' }} />
            </div>
          )}

          {error && !loading && (
            <div
              className="rounded-lg p-3 text-sm"
              style={{
                backgroundColor: 'var(--color-danger-light, rgba(239,68,68,0.1))',
                border: '1px solid var(--color-danger, #ef4444)',
                color: 'var(--color-danger, #ef4444)',
              }}
            >
              {error}
            </div>
          )}

          {!loading && data && (
            <>
              {/* Current snapshot */}
              <div>
                <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  {tp.lastSnapshot}
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  <Metric icon={<Eye className="w-3.5 h-3.5" />} label={tp.impressions} value={formatNumber(current.impressions)} />
                  <Metric icon={<Users className="w-3.5 h-3.5" />} label={tp.reach} value={formatNumber(current.reach)} />
                  <Metric icon={<Heart className="w-3.5 h-3.5" />} label={tp.likes} value={formatNumber(current.likes)} />
                  <Metric icon={<MessageCircle className="w-3.5 h-3.5" />} label={tp.comments} value={formatNumber(current.comments_count)} />
                  <Metric icon={<TrendingUp className="w-3.5 h-3.5" />} label={tp.engagement} value={formatPct(current.engagement_rate)} />
                  <Metric icon={<BarChart3 className="w-3.5 h-3.5" />} label={tp.shares} value={formatNumber(current.shares)} />
                </div>
              </div>

              {/* History timeline (simple table) */}
              {history.length > 1 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    {(tp.history ?? '').replace('{count}', String(history.length))}
                  </h3>
                  <div
                    className="rounded-xl overflow-hidden"
                    style={{ border: '1px solid var(--color-border)' }}
                  >
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ backgroundColor: 'var(--color-surface)' }}>
                          <th className="px-3 py-2 text-left text-xs" style={{ color: 'var(--color-text-secondary)' }}>{tp.colDate}</th>
                          <th className="px-3 py-2 text-right text-xs" style={{ color: 'var(--color-text-secondary)' }}>{tp.colImpressions}</th>
                          <th className="px-3 py-2 text-right text-xs" style={{ color: 'var(--color-text-secondary)' }}>{tp.colLikes}</th>
                          <th className="px-3 py-2 text-right text-xs" style={{ color: 'var(--color-text-secondary)' }}>{tp.colEngagement}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.slice().reverse().slice(0, 10).map((h, i) => (
                          <tr key={h.snapshot_at ?? i} style={{ borderTop: '1px solid var(--color-border)' }}>
                            <td className="px-3 py-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                              {h.snapshot_at ? formatDateLong(h.snapshot_at) : (h.snapshot_date ?? '—')}
                            </td>
                            <td className="px-3 py-2 text-right">{formatNumber(h.impressions)}</td>
                            <td className="px-3 py-2 text-right">{formatNumber(h.likes)}</td>
                            <td className="px-3 py-2 text-right">{formatPct(h.engagement_rate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {history.length === 0 && (
                <div
                  className="rounded-lg p-4 text-sm text-center"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {tp.noSnapshot}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
